import { useEffect, useState } from "react";

export interface ScriptStatusQueryLike {
  isSuccess: boolean;
  isFetching: boolean;
  data?: { exists: boolean };
}

interface UseScriptVerificationOptions {
  autoDetect?: boolean;
  showFailureState?: boolean;
  onVerified?: () => void;
  onCheckSuccess?: () => void;
  onCheckFailure?: () => void;
}

export const useScriptVerification = (
  query: ScriptStatusQueryLike,
  options: UseScriptVerificationOptions = {}
) => {
  const {
    autoDetect = false,
    showFailureState = true,
    onVerified,
    onCheckSuccess,
    onCheckFailure,
  } = options;

  const [verified, setVerified] = useState(false);
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(false);

  // Auto-detect if script is already installed on mount
  useEffect(() => {
    if (
      autoDetect &&
      query.isSuccess &&
      !query.isFetching &&
      query.data?.exists &&
      !verified &&
      !checking
    ) {
      setVerified(true);
      onVerified?.();
    }
  }, [
    autoDetect,
    query.isSuccess,
    query.isFetching,
    query.data?.exists,
    verified,
    checking,
    onVerified,
  ]);

  // Handle user-initiated check results
  useEffect(() => {
    if (!checking || query.isFetching || !query.isSuccess) {
      return;
    }

    if (query.data?.exists) {
      setChecking(false);
      setVerified(true);
      onVerified?.();
      onCheckSuccess?.();
    } else {
      setChecking(false);
      onCheckFailure?.();
      if (showFailureState) {
        setFailed(true);
      }
    }
  }, [
    query.isSuccess,
    query.isFetching,
    query.data?.exists,
    checking,
    showFailureState,
    onVerified,
    onCheckSuccess,
    onCheckFailure,
  ]);

  const triggerCheck = () => {
    setChecking(true);
  };

  const resetFailure = () => {
    setFailed(false);
  };

  return { verified, failed, checking, triggerCheck, resetFailure };
};
